import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, isRef as _isRef, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, watch } from 'vue';
    import { Tabs } from '@/components/ui/tabs';
    import { useFormErrorTabs } from '@/composables/useFormErrorTabs';
    import { useKubeData } from '@/composables/useKubeData';
    import { usePermissions } from '@/composables/usePermissions';
    import CompanyBasisForm from '@/components/companies/CompanyBasisForm.vue';
    import CompanyEconomicForm from '@/components/companies/CompanyEconomicForm.vue';
    import CompanyOutlayForm from '@/components/companies/CompanyOutlayForm.vue';
    import CompanyServiceForm from '@/components/companies/CompanyServiceForm.vue';
    import Permission from '@/enums/Permission';
    import type { Company, User, Tab } from '@/types';
    import { useKeepersEmployeeData } from '@/composables/useKeepersEmployeeData';
    import { useCompanyClientData } from '@/composables/useCompanyClientData';
    import { useVoucherAccountData } from '@/composables/useVoucherAccountData';
    import { useOutlayCompanyData } from '@/composables/useOutlayCompanyData';
    import { useEconomicDimensionsData } from '@/composables/useEconomicDimensionsData';
    import { ref } from 'vue';
    import { EconomicAccount, EconomicDimension } from '@/types';
    import { useCompanyData } from '@/composables/useCompany';
    import { optionsCompanyServices } from '@/utils/form';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'CompanyTabs',
  props: {
    form: {},
    company: {}
  },
  setup(__props: any, { expose: __expose }) {

    const { hasPermission } = usePermissions();

    const props = __props;

    const { kubeOptions } = useKubeData();
    const { keepersEmployees, loadingKeepersEmployees } = useKeepersEmployeeData(props.company?.id);
    const { clientOptions } = useCompanyClientData(props.company?.id);
    const { voucherAccounts, isPendingVoucherAccounts } = useVoucherAccountData(props.company?.id);
    const { outlayCompanies, isPendingOutlayCompanies } = useOutlayCompanyData(props.company?.id);
    const { companyData } = useCompanyData(props.company?.id);
    const { dimensions: economicDimensions, isPendingDimensions: isPendingEconomicDimensions } = useEconomicDimensionsData(props.company?.id);

    const voucherApiAccounts = ref<EconomicAccount[]>([]);

    const voucherOptions = computed((): EconomicAccount[] =>
        voucherApiAccounts.value.length > 0 ? voucherApiAccounts.value : voucherAccounts.value,
    );

    const outlayCompanyOptions = computed(() => {
        if (!Array.isArray(outlayCompanies.value)) return [];
        return outlayCompanies.value;
    });

    watch(companyData, (newData) => {
        props.form.economic_accounts = newData.economic_accounts
            .filter((account: EconomicAccount) => account.approved_post === 1)
            .map((account: EconomicAccount) => account.id);
        props.form.economic_dimensions = newData.economic_dimensions.map((dimension: EconomicDimension) => ({
            ...dimension,
            values: dimension.values ? dimension.values.map((value: any) => ({ ...value })) : []
        }));
        props.form.approver_1 = newData.salary_approval_steps?.first_approver_id;
        props.form.approver_2 = newData.salary_approval_steps?.next_approver_id;
        props.form.outlay_company_id = newData.outlay_integration?.outlay_company_id;
        props.form.services = Object.entries(newData.services)
            .filter(
                ([key, value]) =>
                    value === 1 && optionsCompanyServices.some((option) => option.value === key),
            )
            .map(([key]) => key);
    });

    const tabs = computed(() => {
        const allTabs: Tab[] = [
            { value: 'economic', label: 'companies.economic_tab_text' },
            { value: 'basis', label: 'companies.basic_tab_text' },
            { value: 'service', label: 'companies.services_tab_text' },
            {
                value: 'outlay',
                label: 'companies.outlay_tab_text',
                permissionLevel: Permission.OUTLAY_VIEW,
            },
        ];

        return allTabs.filter(
            (tab) => !tab.permissionLevel || hasPermission(tab.permissionLevel as Permission),
        );
    });

    const fieldToTabMap: Record<string, string> = {
        // Economic tab fields
        endpoint: 'economic',
        api_token: 'economic',
        economic_number: 'economic',
        sign_up_date: 'economic',
        website: 'economic',

        // Basis tab fields
        name: 'basis',
        cvr: 'basis',
        address: 'basis',
        postal_code: 'basis',
        city: 'basis',
        country: 'basis',
        email: 'basis',
        telephone: 'basis',
        mobile_number: 'basis',
        fax_number: 'basis',
        roger_invoices: 'basis',
        roger_receipts: 'basis',
        keepers_email: 'basis',
        employee_default_id: 'basis',
        assistant_id: 'basis',

        // Service tab fields
        services: 'service',
        num_of_vouchers: 'service',
        has_payments: 'service',
        fixed_price: 'service',
        price_frequency: 'service',
        voucher_limit: 'service',
        report_deadline: 'service',
        salary_deadline: 'service',
        vat_frequency: 'service',
        update_frequency: 'service',
        has_e_income: 'service',
        day_frequency: 'service',
        salary_system: 'service',
        salary_two_step_approval: 'service',
        approver_1: 'service',
        approver_2: 'service',
        has_kube: 'service',
        kube_id: 'service',
        to_keepers_kube: 'service',
        kube_data_field_id: 'service',

        // Outlay tab fields
        has_app_service: 'outlay',
    };

    const { currentTab, setTab } = useFormErrorTabs(props.form, fieldToTabMap, 'economic');

    __expose({
        setTab,
    });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Tabs), {
    tabs: tabs.value,
    modelValue: _unref(currentTab),
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(currentTab) ? (currentTab).value = $event : null))
  }, {
    economic: _withCtx(() => [
      _createVNode(CompanyEconomicForm, {
        form: _ctx.form,
        "voucher-accounts": voucherOptions.value,
        "is-pending-voucher-accounts": _unref(isPendingVoucherAccounts),
        dimensions: _unref(economicDimensions),
        "is-pending-dimensions": _unref(isPendingEconomicDimensions),
        onAccountsLoaded: _cache[0] || (_cache[0] = ($event: any) => (voucherApiAccounts.value = $event))
      }, null, 8, ["form", "voucher-accounts", "is-pending-voucher-accounts", "dimensions", "is-pending-dimensions"])
    ]),
    basis: _withCtx(() => [
      _createVNode(CompanyBasisForm, {
        form: _ctx.form,
        "keepers-employees": _unref(keepersEmployees),
        "loading-keepers-employees": _unref(loadingKeepersEmployees)
      }, null, 8, ["form", "keepers-employees", "loading-keepers-employees"])
    ]),
    service: _withCtx(() => [
      _createVNode(CompanyServiceForm, {
        form: _ctx.form,
        "keepers-employees": _unref(keepersEmployees),
        "kube-options": _unref(kubeOptions),
        clients: _unref(clientOptions)
      }, null, 8, ["form", "keepers-employees", "kube-options", "clients"])
    ]),
    outlay: _withCtx(() => [
      _createVNode(CompanyOutlayForm, {
        form: _ctx.form,
        "outlay-companies": outlayCompanyOptions.value,
        "is-pending-outlay-companies": _unref(isPendingOutlayCompanies)
      }, null, 8, ["form", "outlay-companies", "is-pending-outlay-companies"])
    ]),
    _: 1
  }, 8, ["tabs", "modelValue"]))
}
}

})